import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAssetClass } from '../../contexts/assetClass';
import useSWR from 'swr';
import { Asset } from '../../types/asset.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { Loader } from '../../components/loader/Loader';
import { Form } from 'devextreme-react';
import { ButtonItem, ButtonOptions, CustomRule, EmptyItem, GroupItem, Label, RequiredRule, SimpleItem } from 'devextreme-react/form';
import { dxSelectBoxConfig } from '../tasks/TasksForm';
import { useTranslation } from '../../contexts/translation';
import { Task } from '../../types/task.types';
import { CapacityCategory, CapacityCategoryPerson } from '../../types/capacityCategory.types';
import { useIsWorkSheetAllPermission } from '../../hooks/useIsWorkSheetAllPermission';
import { useAuth } from '../../contexts/auth';
import './WorksheetsForm.scss'
import { CONFIG_KEYS, useConfigByKey } from '../../contexts/configContext';
import { WorkActivityGroup } from '../../types/workActivityGroup.types';
import { WorkActivity } from '../../types/workActivity.types';
import { DocumentType } from '../../types/documentType.types';
import { dateTimeFieldOptionsWithSerializationAndInterval } from '../../utils/dates.types';
import moment from 'moment';
import { convertMinsToHrsMins } from '../../pages/Tasks/Columns';
import { set } from 'ramda';
import { FaultType } from '../../types/faultType';
import { TaskStatusApiResponse } from '../../types/TaskStatus';
import { confirm } from 'devextreme/ui/dialog';
import { postApi, putApi } from '../../apiUtils';
import { useHistory } from 'react-router-dom';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';

interface UpdateFormData {
    id: number;
    topLevelParenName?: string;
    assetName?: string;
    taskId: number;
    taskName?: string;
    statusNameTranslated?: string;
    typeNameTranslated?: string;
    responsiblePersonName?: string;
    operatorId: number;
    capacityCategoryId: number;
    workActivityGroupId: number;
    workActivityId: number;
    documentTypeId: number;
    activityStarted?: string;
    activityFinished?: string;
    activityDuration: string | null;
    faultTypeId?: number;
    description?: string;
}

const dateInputFormat = "YYYY-MM-DD HH:mm:ss";

interface Props {
    defaultFormData: UpdateFormData,
    isEditableStatusFlag: boolean;
}

export const WorksheetsEditForm = ({ defaultFormData, isEditableStatusFlag }: Props) => {
    const { assetClass } = useAssetClass();
    const { translate } = useTranslation();
    const transFormName = 'WorkSheet.Update';
    const { user: authUser } = useAuth();
    const history = useHistory();
    const [hasPermissionAllToCreateWorkSheet, loadingPermission] = useIsWorkSheetAllPermission();
    const configRestrictWorkActivitiesByCapacityCategoryPerson = useConfigByKey(
        CONFIG_KEYS.RESTRICT_WA_BY_CAPACITY_CATEGORY,
    );
    const restrictWorkActivitiesByCapacityCategoryPerson = configRestrictWorkActivitiesByCapacityCategoryPerson ? Boolean(configRestrictWorkActivitiesByCapacityCategoryPerson.value === 'True' && configRestrictWorkActivitiesByCapacityCategoryPerson.enabled) : false;
    const now = moment();

    const formRef = useRef<Form>(null);
    const formData = useRef<UpdateFormData>(defaultFormData);

    //#region States
    const [loading, setLoading] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(defaultFormData.taskId || null);
    const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(defaultFormData.operatorId || null);
    const [selectedCapacityCategoryId, setSelectedCapacityCategoryId] = useState<number | null>(defaultFormData.capacityCategoryId || null);
    const [selectedWorkActivityGroupId, setSelectedWorkActivityGroupId] = useState<number | null>(defaultFormData.workActivityGroupId || null);
    const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(defaultFormData.documentTypeId || null);
    const [selectedActivityStarted, setSelectedActivityStarted] = useState<string | null>(defaultFormData.activityStarted || null);
    const [selectedActivityFinished, setSelectedActivityFinished] = useState<string | null>(defaultFormData.activityFinished || null);
    //#endregion

    const [
        prompt,
        setDirty,
        setClean,
        handleCloseButton
    ] = useUnsavedChangesWarning('', () => {
        if (formRef.current!.instance.validate().isValid)
            onSubmit(null).then(() => {
                history.goBack();
            });
    });

    //#region API calls
    // API seznam tasks
    const { data: tasks, isValidating: isValidatingTasks, } = useSWR<Task[]>(
        `${API_URL.TASK_BY_CLASS_WORKSHEET_CREATE_EDIT}?classId=${assetClass}&statusIdArr=10&statusIdArr=20&statusIdArr=50`,
        fetcher,
        swrConfig,
    );


    // API seznam operators
    const { data: operators, isValidating: isValidatingOperators } = useSWR<
        CapacityCategoryPerson[]
    >(`${API_URL.CAPACITY_CATEGORY_PERSON_LIST}?visibleOnly=true`, fetcher, swrConfig);

    // API capacity categories
    const { data: capacityCategories, isValidating: isValidatingCapacityCategories } = useSWR<CapacityCategory[]>(
        `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`, fetcher, swrConfig,
    );

    // API work activity groups
    const { data: workActivityGroups, isValidating: isValidatingWorkActivityGroups } = useSWR<WorkActivityGroup[]>(
        selectedCapacityCategoryId
            ? `${API_URL.WORK_ACTIVITY_GROUP_LIST_BY_CAPACITY}?capacityCategoryId=${selectedCapacityCategoryId
            }${selectedDocumentTypeId ? `&documentTypeId=${selectedDocumentTypeId}` : ''}`
            : null,
        fetcher, swrConfig,
    );

    // API work activity
    const { data: workActivities, isValidating: isValidatingWorkActivities } = useSWR<WorkActivity[]>(
        `${API_URL.WORK_ACTIVITY_LIST}`, fetcher, swrConfig,
    );

    // API worksheet task time validation
    const { data: disableWorksheetTaskTimeValidation, isValidating: isValidatingDisableWorksheetTaskTimeValidation } = useSWR<boolean>(
        `${API_URL.GET_CONFIG_IS_ENABLED}?name=disableWorksheetTaskTimeValidation`, fetcher, swrConfig
    );

    // API fault types
    const { data: faultTypes, isValidating: isValidatingFaultTypes } = useSWR<FaultType[]>(
        `${API_URL.FAULT_TYPE_LIST}`, fetcher, swrConfig
    );

    // API document types
    const { data: documentTypes, isValidating: isValidatingDocumentTypes } = useSWR<DocumentType[]>(
        `${API_URL.DOCUMENT_TYPE_LIST}?parentId=${assetClass}&visibleOnly=true`, fetcher, swrConfig,
    );

    // API task process config
    const { data: taskProcessConfig, isValidating: isValidatingTaskProcessConfig } = useSWR<TaskStatusApiResponse>(
        `${API_URL.TASK_PROCESS_CONFIG_LIST}?visibleOnly=${true}`, fetcher, swrConfig
    );

    //#endregion

    //#region UseEffects

    //#endregion

    //#region Handlers
    // Při výběru prvního operátora se smaže výběr kapacitní kategorie. Kvůli profiltrování.
    // Výběr mažeme pouze pokud se kapacitní kategorie filtrují = config
    // Mažeme i work activity group a work activity
    const handleOperatorChange = useCallback((operatorId: number) => {
        if (restrictWorkActivitiesByCapacityCategoryPerson) {
            formRef.current?.instance.updateData("capacityCategoryId", null);
            formRef.current?.instance.updateData("workActivityGroupId", null);
            formRef.current?.instance.updateData("workActivityId", null);

            setSelectedCapacityCategoryId(null);
            setSelectedWorkActivityGroupId(null);
        }
        setSelectedOperatorId(operatorId);
    }, [restrictWorkActivitiesByCapacityCategoryPerson])

    // Při změně kapacitní kategorie se refetche API activity groups a logika předvyplňování je v useEffect    
    const handleCapacityCategoryChange = useCallback((capacityCategoryId: number | null) => {
        if (!capacityCategoryId) {
            formRef.current?.instance.updateData("workActivityGroupId", null);
            formRef.current?.instance.updateData("workActivityId", null);
            setSelectedWorkActivityGroupId(null);
        }
        setSelectedCapacityCategoryId(capacityCategoryId);
    }, [])

    // Při změně skupiny aktivity promažeme aktivitu
    // Pokud je právě jedna aktivita, vybereme ji
    const handleWorkActivityGroupChange = useCallback((workActivityGroupId: number | null) => {
        let activity: any = null;
        if (workActivities && workActivityGroupId && workActivities.filter(x => x.workActivityGroupId === workActivityGroupId).length === 1)
            activity = workActivities.filter(x => x.workActivityGroupId === workActivityGroupId)[0];

        setSelectedWorkActivityGroupId(workActivityGroupId);
        formRef.current?.instance.updateData("workActivityId", activity ? activity.id : null);
    }, [workActivities])

    // Při změně začátku aktivity přepočítáme duration
    const handleActivityStartedChange = useCallback((activityStarted: string) => {
        calculateDuration(activityStarted, selectedActivityFinished);
        setSelectedActivityStarted(activityStarted);
    }, [selectedActivityFinished])

    // Při změně konce aktivity přepočítáme duration
    const handleActivityFinishedChange = useCallback((activityFinished: string) => {
        calculateDuration(selectedActivityStarted, activityFinished);
        setSelectedActivityFinished(activityFinished);
    }, [selectedActivityStarted])

    // Ručně se změnilo duration, přepočítá se activityFinished, případně activityStarted podle konfigurace
    const handleActivityDurationChange = useCallback((e: any) => {
        // Transformace vstupu - přepočet na HH:mm
        let value = e.value.trim().replace(',', '.').replace(':', '.');
        if (value.includes('.')) {
            const valueArr = value.split('.');
            value = convertMinsToHrsMins(Number(valueArr[0]) * 60 + Number(valueArr[1]));
        } else
            value = convertMinsToHrsMins(value);

        e.component.option('value', value);

        const started = moment(selectedActivityStarted);
        const durationParts = value.split(':');
        const duration = moment.duration({ hours: Number(durationParts[0]), minutes: Number(durationParts[1]) });

        // 24.6.2025 Chování s konfigem - Vezmeme started + duration. 
        // Pokud výsledek není v budoucnosti, standartně se dopočítá datum do a datum od zůstane.
        // Pokud je do budoucnosti, tak datum do nastavíme na now a datum od je now - duration.
        if (disableWorksheetTaskTimeValidation) {
            const finished = started.clone().add(duration);
            if (finished.isAfter(now)) {
                formRef.current?.instance.updateData("activityFinished", now.format(dateInputFormat));
                const newStarted = now.clone().subtract(duration);
                formRef.current?.instance.updateData("activityStarted", newStarted.format(dateInputFormat));

                setSelectedActivityStarted(newStarted.format(dateInputFormat));
                setSelectedActivityFinished(now.format(dateInputFormat));
            } else {
                formRef.current?.instance.updateData("activityFinished", finished.format(dateInputFormat));
                setSelectedActivityFinished(finished.format(dateInputFormat));
            }
        }
        // Standardní chování - datum do je started + duration
        else {
            const finished = started.clone().add(duration);
            formRef.current?.instance.updateData("activityFinished", finished.format(dateInputFormat));
            setSelectedActivityFinished(finished.format(dateInputFormat));
        }
    }, [disableWorksheetTaskTimeValidation, selectedActivityStarted])

    // Nastaví stav worksheetu na 0 tzn. Canceled
    const handleCancelButton = useCallback(async () => {
        setLoading(true);

        await putApi({
            url: API_URL.WORKSHEET_PUT,
            data: { ...formData.current, workSheetStatusId: 0 },
            callAfterSuccess: async () => {
            },
        });

        setLoading(false);
    }, []);
    //#endregion 

    //#region Helpers function
    // V operators jsou duplicitní záznamy. Pro jednoho operátora může být více kapacitních kategorií
    // Pokud uživatel nemá právo WfWorksheetAll, může založit jen na sebe
    const getOperators = useCallback(() => {
        if (!operators || !authUser) return [];
        let _operators = operators.filter((item, index, self) =>
            index === self.findIndex((t) => t.personId === item.personId)
        );

        if (!hasPermissionAllToCreateWorkSheet)
            _operators = operators.filter(x => x.personId === authUser.personalNumber);

        return _operators;
    }, [hasPermissionAllToCreateWorkSheet, operators, authUser])

    const capacityCategoriesFiltered = useMemo(() => {
        if (!capacityCategories || !operators) return [];
        let _capacityCategories = capacityCategories;

        if (restrictWorkActivitiesByCapacityCategoryPerson)
            _capacityCategories = _capacityCategories.filter(
                (x) =>
                    operators.filter(
                        (y) => y.personId === selectedOperatorId && y.capacityCategoryId === x.id,
                    ).length > 0,
            );

        return _capacityCategories;
    }, [capacityCategories, operators, restrictWorkActivitiesByCapacityCategoryPerson, selectedOperatorId])

    // Podle activityStrted a activityFinished spočítáme duration
    const calculateDuration = useCallback((activityStarted: string | null, activityFinished: string | null) => {
        // Pro výpočet potřebujeme obě hodnoty, jinak nulujeme
        if (!activityStarted || !activityFinished) {
            formRef.current?.instance.updateData("activityDuration", null);
            return;
        }

        const start = moment(activityStarted);
        const finish = moment(activityFinished);

        // Pokud je start větší než finish, nulujeme
        if (start.isAfter(finish)) {
            formRef.current?.instance.updateData("activityDuration", null);
            return;
        }

        const duration = moment.duration(finish.diff(start));
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();
        const durationStr = `${(hours < 10 ? '0' + hours : hours)}:${(minutes < 10 ? '0' + minutes : minutes)}`;

        formRef.current?.instance.updateData("activityDuration", durationStr);
    }, [selectedActivityStarted, selectedActivityFinished])

    // Validace vstupu duration. Je možné zadat HH:mm nebo HH.mm nebo HH,mm
    const isDurationValid = (value: any): boolean => {
        if (value === undefined || value === null || value === "") return true;
        const normalized = String(value).replace(',', '.').replace(':', '.');
        return !Number.isNaN(Number(normalized));
    };

    // Fault type se zobrazuje pouze pokud má zvolený document type taskShowFaultType = true
    // Pokud se skryje, vynulujeme hdonotu
    const isFaultTypeVisible = useCallback(() => {
        if (!documentTypes || !selectedDocumentTypeId) {
            formRef.current?.instance.updateData("faultTypeId", null);
            return false;
        }

        const documentType = documentTypes.find(dt => dt.id === selectedDocumentTypeId);
        if (!documentType) {
            formRef.current?.instance.updateData("faultTypeId", null);
            return false;
        }

        return documentType.taskShowFaultType;
    }, [documentTypes, selectedDocumentTypeId]);

    // Logika, jestli má být vidět taksComplete checkbox a jestli má být defaultně checknutý
    const processTaskComplete = useCallback(() => {
        console.log('------ PROCESS TaskComplete ------');
        // Pokud není vyplněn Activity Finished výkazu, nikdy nechceme dát možnost ukončit výkaz. Tzn. není vidět.
        if (!selectedActivityFinished) {
            console.log('Není vybrán Activity Finished = skryjeme.');
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
            return false;
        }

        // Activity Finished je vyplněno a není vybraný task, checkbox zobrazíme.
        if (!selectedTaskId) {
            console.log('Není task = zobrazíme.');
            // Kontrolujeme worksheetTaskCompleteBehavior na document type
            // a) worksheetTaskCompleteBehavior = 0, tak se nastaví checkbox na false.
            // b) worksheetTaskCompleteBehavior = 2, tak se nastaví checkbox na true.
            // c) Pokud neplatí nic z předchozího a není vybrán task, chceme defaultně checknout;
            // d) Pokud neplatí nic z předchozího a task je vybrán. Checkneme checknout pouze pokud se zodpovědná osoba tasku rovná operátorovi výkazu. -> Nemůže nastat v této větvi, protože není task.
            if (documentTypes && selectedDocumentTypeId) {
                const documentType = documentTypes.find(x => x.id === selectedDocumentTypeId);
                console.log('documentType', documentType);
                if (documentType && documentType.worksheetTaskCompleteBehavior === 0) {
                    console.log(`Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 0.`);
                    console.log('taskComplete = false');
                    formRef.current?.instance.updateData("taskComplete", false);
                } else if (documentType && documentType.worksheetTaskCompleteBehavior === 2) {
                    console.log(`Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 1.`);
                    console.log('taskComplete = true');
                    formRef.current?.instance.updateData("taskComplete", true);
                } else {
                    console.log(`Vybraný document type ${selectedDocumentTypeId} nemá specifikovaný worksheetTaskCompleteBehavior ${documentType?.worksheetTaskCompleteBehavior} a není vybrán task.`);
                    console.log('taskComplete = true');
                    formRef.current?.instance.updateData("taskComplete", true);
                }
            } else {
                console.log(`Není vybraný document type nebo se nenačetly = nemůžeme zjistit behavior.`);
                console.log('taskComplete = false');
                formRef.current?.instance.updateData("taskComplete", false);
            }
            return true;
        }

        // Activity Finished je vyplněno a je vybraný task. Podíváme se, jestli je možné task ukončit tzn. existuje actionId = 14. Pokud ano, zobrazíme checkbox.
        // Pokud by náhodou neexistoval document type nebo taskProcessConfig, skryjeme checkbox.
        if (!documentTypes || !taskProcessConfig || !tasks) {
            console.log('Nejsou načteny documen types nebo process config nebo tasky = skryjeme.');
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
            return false;
        }

        // Kontrola jestli je vybraný document type
        if (!selectedDocumentTypeId) {
            console.log('Není vybraný document type = skryjeme.');
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
            return false;
        }

        // Načteme document type
        const documentType = documentTypes.find(x => x.id === selectedDocumentTypeId);
        if (!documentType) {
            console.log(`Nepovedlo se nalézt document type ${selectedDocumentTypeId} = skryjeme.`);
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
            return false;
        }

        // Načteme task
        const task = tasks.find(x => x.id === selectedTaskId);
        if (!task) {
            console.log(`Nepovedlo se nalézt task ${selectedTaskId} = skryjeme.`);
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
            return false;
        }

        // Kontrola v task process configu
        console.log(`Hledáme v task process configu pro documentTypeId ${documentType.id}, statusFromId ${task.statusId} a actionId 14.`);
        const taskConfig = taskProcessConfig.find(x => x.documentTypeId === documentType.id && x.statusFromId === task.statusId && x.actionId === 14);
        if (!taskConfig) {
            console.log(`Nepovedlo se nalézt v process configu = skryjeme.`);
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
            return false;
        }

        console.log(`Povedlo se nalézt v process configu = zobrazíme.`);
        // Kontrolujeme taskDocumentTypeCompleteBehavior na document type
        // a) worksheetTaskCompleteBehavior = 0, tak se nastaví checkbox na false.
        // b) worksheetTaskCompleteBehavior = 2, tak se nastaví checkbox na true.
        // c) Pokud neplatí nic z předchozího a není vybrán task, chceme defaultně checknout. -> Nemůže nastat v této větvi, protože musí být task.
        // d) Pokud neplatí nic z předchozího a task je vybrán. Checkneme checknout pouze pokud se zodpovědná osoba tasku rovná operátorovi výkazu. 
        if (documentTypes && selectedDocumentTypeId) {
            const documentType = documentTypes.find(x => x.id === selectedDocumentTypeId);
            if (documentType && documentType.worksheetTaskCompleteBehavior === 0) {
                console.log(`Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 0.`);
                console.log('taskComplete = false');
                formRef.current?.instance.updateData("taskComplete", false);
            } else if (documentType && documentType.worksheetTaskCompleteBehavior === 2) {
                console.log(`Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 1.`);
                console.log('taskComplete = true');
                formRef.current?.instance.updateData("taskComplete", true);
            } else if (selectedOperatorId && task.responsiblePersonId === selectedOperatorId) {
                console.log(`Vybraný document type ${selectedDocumentTypeId} nemá specifikovaný worksheetTaskCompleteBehavior ${documentType?.worksheetTaskCompleteBehavior}. Task je vybrán, kontroluej se, jestli vybraný operator ${selectedOperatorId} se rovná zodpovědné osobě na tasku ${task.responsiblePersonId}.`);
                console.log('taskComplete = true');
                formRef.current?.instance.updateData("taskComplete", true);
            }
        } else {
            console.log(`Není vybraný document type nebo se nenačetly = nemůžeme zjistit behavior.`);
            console.log('taskComplete = false');
            formRef.current?.instance.updateData("taskComplete", false);
        }
        return true;
    }, [selectedActivityFinished, selectedTaskId, selectedDocumentTypeId, taskProcessConfig, tasks, documentTypes]);
    //#endregion

    //#region Submit
    const onSubmit = useCallback(async (e: any) => {
        if (e) e.preventDefault();

        setLoading(true);

        await putApi({
            url: API_URL.WORKSHEET_PUT,
            data: { ...formData.current },
            callAfterSuccess: async () => {
            },
        });

        setClean();
        setLoading(false);
    }, []);
    //#endregion    

    if (isValidatingTasks || isValidatingOperators || isValidatingCapacityCategories || isValidatingWorkActivityGroups || isValidatingWorkActivities
        || isValidatingDisableWorksheetTaskTimeValidation || isValidatingFaultTypes || isValidatingDocumentTypes || isValidatingTaskProcessConfig) return <Loader />

    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <Form
                    colCount={3}
                    formData={formData.current}
                    showValidationSummary={true}
                    disabled={loading}
                    ref={formRef}
                    onFieldDataChanged={(e) => {
                        if (e.dataField !== "activityFinished" && e.dataField !== "taskComplete") setDirty();
                    }}
                >
                    <SimpleItem
                        dataField="topLevelParenName"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: true,
                        }}
                    >
                        <Label text={translate!('Line name', transFormName)} />
                    </SimpleItem>
                    <SimpleItem
                        dataField="assetName"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: true,
                        }}
                    >
                        <Label text={translate!('Asset name', transFormName)} />
                    </SimpleItem>
                    <SimpleItem
                        dataField="taskName"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: true,
                        }}
                    >
                        <Label text={translate!('Task name', transFormName)} />
                    </SimpleItem>
                    <SimpleItem
                        dataField="statusNameTranslated"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: true,
                        }}
                    >
                        <Label text={translate!('Status Name', transFormName)} />
                    </SimpleItem>
                    <SimpleItem
                        dataField="typeNameTranslated"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: true,
                        }}
                    >
                        <Label text={translate!('Document Type Name', transFormName)} />
                    </SimpleItem>
                    <SimpleItem
                        dataField="responsiblePersonName"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: true,
                        }}
                    >
                        <Label text={translate!('Responsible Person Name', transFormName)} />
                    </SimpleItem>

                    <EmptyItem colSpan={3} cssClass={'workSheetDivider'}></EmptyItem>

                    <SimpleItem
                        dataField={'operatorId'}
                        editorType={'dxSelectBox'}
                        editorOptions={{
                            ...dxSelectBoxConfig,
                            valueExpr: 'personId',
                            displayExpr: 'personName',
                            dataSource: getOperators(),
                            onValueChanged: (e: any) => {
                                if (!e.event) return;
                                handleOperatorChange(e.value);
                            }
                        }}
                    >
                        <Label text={translate!('Operator', transFormName)} />
                        <RequiredRule message={translate!('Operator required', transFormName)} />
                    </SimpleItem>
                    <EmptyItem />
                    <EmptyItem />
                    <SimpleItem
                        dataField={'capacityCategoryId'}
                        editorType={'dxSelectBox'}
                        editorOptions={{
                            ...dxSelectBoxConfig,
                            valueExpr: 'id',
                            displayExpr: 'name',
                            dataSource: capacityCategoriesFiltered,
                            onValueChanged: (e: any) => {
                                if (!e.event) return;
                                handleCapacityCategoryChange(e.value);
                            }
                        }}
                    >
                        <Label text={translate!('Capacity Category Name', transFormName)} />
                        <RequiredRule message={translate!('Select capacity category', transFormName)} />
                    </SimpleItem>
                    <SimpleItem
                        dataField={'workActivityGroupId'}
                        editorType={'dxSelectBox'}
                        editorOptions={{
                            ...dxSelectBoxConfig,
                            valueExpr: 'id',
                            displayExpr: 'name',
                            dataSource: selectedCapacityCategoryId ? workActivityGroups : [],
                            onValueChanged: (e: any) => {
                                if (!e.event) return;
                                handleWorkActivityGroupChange(e.value);
                            }
                        }}
                    >
                        <Label text={translate!('Activity group', transFormName)} />
                        <RequiredRule message={translate!('Activity group required', transFormName)} />
                    </SimpleItem>

                    <SimpleItem
                        dataField={'workActivityId'}
                        editorType={'dxSelectBox'}
                        editorOptions={{
                            ...dxSelectBoxConfig,
                            valueExpr: 'id',
                            displayExpr: 'name',
                            dataSource: selectedWorkActivityGroupId ? workActivities?.filter(x => x.workActivityGroupId === selectedWorkActivityGroupId) : [],
                        }}
                    >
                        <Label text={translate!('Activity', transFormName)} />
                        <RequiredRule message={translate!('Activity required', transFormName)} />
                    </SimpleItem>

                    <SimpleItem
                        dataField="activityStarted"
                        editorType="dxDateBox"
                        editorOptions={{
                            ...dateTimeFieldOptionsWithSerializationAndInterval,
                            readOnly: !isEditableStatusFlag,
                            onValueChanged: (e: any) => {
                                if (!e.event) return;
                                handleActivityStartedChange(e.value);
                            },
                            disabledDates: (args) => {
                                // Configem lze vypnout kontrolu
                                if (disableWorksheetTaskTimeValidation) return false;
                                // Pokud není task, nekontorlujeme
                                if (!selectedTaskId) return false;
                                // Nedovolíme jít do minulosti od min(TaskCreate, Task.Started)
                                const task = tasks?.find(x => x.id === selectedTaskId);
                                if (!task) return false;

                                let dateToCompare: string = task.created.toString();
                                if (task.started && moment(task.started).isBefore(moment(task.created)))
                                    dateToCompare = task.started;

                                return moment(args.date).isBefore(moment(dateToCompare).startOf('day'));
                            },
                        }}
                    >
                        <Label text={translate!('Activity started', transFormName)} />
                        <RequiredRule message={translate!('Activity started required', transFormName)} />
                        <CustomRule
                            validationCallback={(e) => {
                                // Configem lze vypnout kontrolu
                                if (disableWorksheetTaskTimeValidation) return true;
                                // Pokud není task, nekontorlujeme
                                if (!selectedTaskId) return true;
                                // Nedovolíme jít do minulosti od min(TaskCreate, Task.Started)
                                const task = tasks?.find(x => x.id === selectedTaskId);
                                if (!task) return true;

                                let dateToCompare: string = task.created.toString();
                                if (task.started && moment(task.started).isBefore(moment(task.created)))
                                    dateToCompare = task.started;

                                return moment(e.value).endOf('minute').isSameOrAfter(moment(dateToCompare));
                            }}
                            type={'custom'}
                            message={translate!(
                                'Date is invalid, must be greater then now or greater than task create date',
                                transFormName,
                            )}
                            ignoreEmptyValue
                        />
                    </SimpleItem>
                    <SimpleItem
                        dataField="activityFinished"
                        editorType="dxDateBox"
                        editorOptions={{
                            ...dateTimeFieldOptionsWithSerializationAndInterval,
                            readOnly: !isEditableStatusFlag,
                            onValueChanged: (e: any) => {
                                if (!e.event) return;
                                handleActivityFinishedChange(e.value);
                            },
                            disabledDates: (args) => {
                                // Nedovolíme do budoucnosti a nesmí být před activityStarted
                                return (
                                    moment(args.date).isAfter(now.clone().endOf('minute'))
                                    || (selectedActivityStarted && moment(args.date).endOf('day').isBefore(moment(selectedActivityStarted).startOf('day')))
                                );
                            },
                        }}
                    >
                        <Label text={translate!('Activity finished', transFormName)} />
                        <CustomRule
                            validationCallback={(e) => {
                                // Nedovolíme do budoucnosti a nesmí být před activityStarted                            
                                return (
                                    moment(e.value).startOf('minute').isSameOrBefore(now)
                                    && (!selectedActivityStarted || moment(e.value).isSameOrAfter(moment(selectedActivityStarted)))
                                );
                            }}
                            type={'custom'}
                            message={translate!(
                                'Date is invalid, must be lower then now and can not be before activity start',
                                transFormName,
                            )}
                            ignoreEmptyValue
                        />
                    </SimpleItem>
                    <SimpleItem
                        dataField="activityDuration"
                        editorType="dxTextBox"
                        editorOptions={{
                            readOnly: !isEditableStatusFlag,
                            onValueChanged: (e: any) => {
                                if (e.event && isDurationValid(e.value))
                                    handleActivityDurationChange(e);
                            },
                        }}
                    >
                        <Label text={translate!('Duration', transFormName)} />
                        <CustomRule validationCallback={(e) => isDurationValid(e.value)} />
                    </SimpleItem>

                    {isFaultTypeVisible() && (
                        <SimpleItem
                            dataField="faultTypeId"
                            editorType="dxSelectBox"
                            editorOptions={{
                                ...dxSelectBoxConfig,
                                valueExpr: 'id',
                                displayExpr: 'name',
                                items: faultTypes,
                            }}
                        >
                            <Label text={translate!('Fault type', transFormName)} />
                        </SimpleItem>
                    )}

                    {isFaultTypeVisible() && <EmptyItem />}
                    {isFaultTypeVisible() && <EmptyItem />}


                    <SimpleItem
                        colSpan={3}
                        dataField="description"
                        editorType="dxTextArea"
                        editorOptions={{
                            readOnly: !isEditableStatusFlag,
                            height: 100,
                        }}
                    >
                        <Label text={translate!('Description', transFormName)} />
                    </SimpleItem>

                    {processTaskComplete() && (
                        <SimpleItem colSpan={3} dataField="taskComplete" editorType="dxCheckBox">
                            <Label text={translate!('Task complete', transFormName)} />
                        </SimpleItem>
                    )}

                    <GroupItem colCount={isEditableStatusFlag ? 3 : 1} colSpan={3} cssClass={'desktop-save-edit-buttons'}>
                        <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
                            <ButtonOptions
                                width={'100%'}
                                text={translate!('Close', 'Asset.Update')}
                                type={'normal'}
                                useSubmitBehavior={false}
                                onClick={handleCloseButton}
                                disabled={loading}
                            />
                        </ButtonItem>
                        <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
                            <ButtonOptions
                                width={'100%'}
                                text={translate!('Save', transFormName)}
                                type={'success'}
                                useSubmitBehavior={false}
                                onClick={() => {
                                    if (formRef.current?.instance.validate().isValid)
                                        onSubmit(null)
                                }}
                                disabled={loading}
                                visible={isEditableStatusFlag}
                            />
                        </ButtonItem>
                        <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
                            <ButtonOptions
                                width={'100%'}
                                text={translate!('Delete', transFormName)}
                                type={'danger'}
                                onClick={() => handleCancelButton()}
                                disabled={loading}
                                visible={isEditableStatusFlag}
                                useSubmitBehavior={false}
                            />
                        </ButtonItem>
                    </GroupItem>
                </Form>
            </form>
            {prompt}
        </>
    )
}